import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Spinner, Alert, Table, Form, Button, Badge, Pagination } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';

const SecretaryDataChanges = () => {
  const [dataChanges, setDataChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    start_date: '',
    end_date: '',
    operation_type: '',
    table_name: '',
    field_name: ''
  });
  const [pagination, setPagination] = useState({});
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 檢查權限 - 只有秘書和管理員可以查看
  useEffect(() => {
    if (!user || (!isSuperAdmin(user.e_id) && user.e_position !== 17)) {
      setError('權限不足，只有秘書和管理員可以查看資料異動記錄');
      setLoading(false);
      return;
    }
    fetchDataChanges();
  }, [user, filters]);

  // 獲取社友資料異動記錄
  const fetchDataChanges = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/secretary-data-changes', {
        headers: { Authorization: `Bearer ${token}` },
        params: filters
      });
      
      setDataChanges(response.data.data || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (error) {
      console.error('獲取資料異動記錄失敗:', error);
      setError(error.response?.data?.message || '獲取資料異動記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  // 處理篩選條件變更
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // 重置頁碼
    }));
  };

  // 重置篩選條件
  const resetFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      search: '',
      start_date: '',
      end_date: '',
      operation_type: '',
      table_name: '',
      field_name: ''
    });
  };

  // 處理分頁
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // 格式化操作類型
  const formatOperationType = (type) => {
    const typeMap = {
      'INSERT': '新增',
      'UPDATE': '修改',
      'DELETE': '刪除',
      'CREATE': '新增' // 相容性
    };
    return typeMap[type] || type;
  };

  // 格式化表格名稱
  const formatTableName = (tableName) => {
    const tableMap = {
      'employees': '社友資料',
      'activities': '活動資料',
      'participations': '參與記錄',
      'companies': '公司資料',
      'lecturers': '講師資料'
    };
    return tableMap[tableName] || tableName;
  };

  // 格式化欄位名稱
  const formatFieldName = (fieldName) => {
    const fieldMap = {
      'e_name': '姓名',
      'e_mobile': '手機',
      'e_email': '電子郵件',
      'e_comp_name': '公司名稱',
      'e_comp_position': '公司職位',
      'e_address1': '公司地址',
      'e_photo': '照片',
      'EDM_photo': 'EDM照片',
      'e_line_id': 'LINE ID'
    };
    return fieldMap[fieldName] || fieldName;
  };

  // 格式化日期時間
  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 渲染分頁組件
  const renderPagination = () => {
    if (!pagination.totalPages || pagination.totalPages <= 1) return null;

    const items = [];
    const current = pagination.currentPage;
    const total = pagination.totalPages;

    // 上一頁
    items.push(
      <Pagination.Prev 
        key="prev" 
        disabled={current <= 1}
        onClick={() => handlePageChange(current - 1)}
      />
    );

    // 頁碼
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // 下一頁
    items.push(
      <Pagination.Next 
        key="next" 
        disabled={current >= total}
        onClick={() => handlePageChange(current + 1)}
      />
    );

    return <Pagination>{items}</Pagination>;
  };

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                📋 社友資料異動查詢
                {user?.e_ssid && (
                  <Badge bg="light" text="dark" className="ms-2">
                    限制查看本社資料
                  </Badge>
                )}
              </h4>
            </Card.Header>
            <Card.Body>
              {/* 篩選區域 */}
              <Row className="mb-4">
                <Col md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>搜尋關鍵字</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="搜尋操作者、目標用戶或描述..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>開始日期</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>結束日期</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>操作類型</Form.Label>
                    <Form.Select
                      value={filters.operation_type}
                      onChange={(e) => handleFilterChange('operation_type', e.target.value)}
                    >
                      <option value="">全部</option>
                      <option value="CREATE">新增</option>
                      <option value="UPDATE">修改</option>
                      <option value="DELETE">刪除</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Group>
                    <Form.Label>資料表</Form.Label>
                    <Form.Select
                      value={filters.table_name}
                      onChange={(e) => handleFilterChange('table_name', e.target.value)}
                    >
                      <option value="">全部</option>
                      <option value="employees">社友資料</option>
                      <option value="activities">活動資料</option>
                      <option value="participations">參與記錄</option>
                      <option value="companies">公司資料</option>
                      <option value="lecturers">講師資料</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={1} className="mb-3 d-flex align-items-end">
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    重置
                  </Button>
                </Col>
              </Row>

              {/* 統計資訊 */}
              {pagination.total !== undefined && (
                <Alert variant="info" className="mb-3">
                  <strong>查詢結果：</strong>共找到 {pagination.total} 筆資料異動記錄
                  {filters.search && ` | 搜尋: "${filters.search}"`}
                  {filters.operation_type && ` | 操作: ${formatOperationType(filters.operation_type)}`}
                  {filters.table_name && ` | 表格: ${formatTableName(filters.table_name)}`}
                </Alert>
              )}

              {/* 資料表格 */}
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">載入中...</span>
                  </Spinner>
                </div>
              ) : dataChanges.length > 0 ? (
                <>
                  <Table responsive striped hover>
                    <thead className="bg-light">
                      <tr>
                        <th>異動時間</th>
                        <th>操作者</th>
                        <th>操作類型</th>
                        <th>資料表</th>
                        <th>異動欄位</th>
                        <th>目標用戶</th>
                        <th>異動內容</th>
                        <th>IP 地址</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataChanges.map((record, index) => (
                        <tr key={index}>
                          <td>
                            <small>{formatDateTime(record.created_at)}</small>
                          </td>
                          <td>
                            <div>
                              <strong>{record.operator_name}</strong>
                              <br />
                              <small className="text-muted">ID: {record.operator_id}</small>
                            </div>
                          </td>
                          <td>
                            <Badge bg={
                              record.operation_type === 'INSERT' || record.operation_type === 'CREATE' ? 'success' :
                              record.operation_type === 'UPDATE' ? 'primary' :
                              record.operation_type === 'DELETE' ? 'danger' : 'secondary'
                            }>
                              {formatOperationType(record.operation_type)}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="info" className="text-white">
                              {formatTableName(record.table_name)}
                            </Badge>
                          </td>
                          <td>
                            <code style={{ fontSize: '11px', color: '#6f42c1' }}>
                              {formatFieldName(record.field_name)}
                            </code>
                          </td>
                          <td>
                            {record.target_user_name && (
                              <div>
                                <strong>{record.target_user_name}</strong>
                                <br />
                                <small className="text-muted">ID: {record.record_id}</small>
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ maxWidth: '200px' }}>
                              {record.old_value && (
                                <div className="mb-1">
                                  <small className="text-muted">舊值:</small>
                                  <div style={{ 
                                    backgroundColor: '#fff2f2', 
                                    padding: '2px 4px', 
                                    borderRadius: '3px',
                                    fontSize: '11px',
                                    wordBreak: 'break-all'
                                  }}>
                                    {record.old_value.length > 30 ? 
                                      record.old_value.substring(0, 30) + '...' : 
                                      record.old_value
                                    }
                                  </div>
                                </div>
                              )}
                              {record.new_value && (
                                <div>
                                  <small className="text-muted">新值:</small>
                                  <div style={{ 
                                    backgroundColor: '#f2fff2', 
                                    padding: '2px 4px', 
                                    borderRadius: '3px',
                                    fontSize: '11px',
                                    wordBreak: 'break-all'
                                  }}>
                                    {record.new_value.length > 30 ? 
                                      record.new_value.substring(0, 30) + '...' : 
                                      record.new_value
                                    }
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">{record.ip_address}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* 分頁 */}
                  <div className="d-flex justify-content-center mt-4">
                    {renderPagination()}
                  </div>
                </>
              ) : (
                <Alert variant="warning" className="text-center">
                  沒有找到符合條件的資料異動記錄
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SecretaryDataChanges;